import { useEffect, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import i18n from './i18n'
import { LANGUAGE_STORAGE_KEY } from './i18n'
import {
  LanguageContext,
  resolveLanguage,
  type AppLanguage,
} from './use-language'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useBootstrapQuery } from '@/features/bootstrap/api/bootstrap/queries/bootstrap-query'
import { apiClient } from '@/shared/api/client'

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() =>
    resolveLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY)),
  )
  const { user } = useAuth()
  const bootstrapQuery = useBootstrapQuery()
  const serverLanguage = bootstrapQuery.data?.user.language

  // Sync from server profile on load
  useEffect(() => {
    if (serverLanguage) {
      const resolved = resolveLanguage(serverLanguage)
      if (resolved !== language) {
        setLanguageState(resolved)
        i18n.changeLanguage(resolved)
        localStorage.setItem(LANGUAGE_STORAGE_KEY, resolved)
      }
    }
  }, [serverLanguage]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep i18n in sync whenever language state changes
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language)
    }
  }, [language])

  const setLanguage = useCallback(
    (lang: AppLanguage) => {
      setLanguageState(lang)
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
      i18n.changeLanguage(lang)

      if (user) {
        apiClient.user.updatePreferences({ language: lang }).catch(() => {
          // silently fail — localStorage already saved
        })
      }
    },
    [user],
  )

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}
